import React, { useContext, useEffect, useRef, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { setSearchQuery, toggleCustomizationPanel, updateChatCustomization } from '../../reducers/chatSlice';
import { AuthContext } from '../../context/AuthContext';
import { useChatRoom } from '../../hooks/useChatRoom';
import useChatSelection from '../../hooks/useChatSelection';
import useReceiverMeta from '../../hooks/useReceiverMeta';
import { usePresence } from '../../hooks/usePresence';

import ChatHeader from '../../components/chat/ChatHeader';
import ChatInput from '../../components/chat/ChatInput';
import CustomizationPanel from '../../components/chat/CustomizationPanel';
import ChatMessageList from '../../components/chat/ChatMessageList';
import SelectionHeader from '../../components/chat/SelectionHeader';
import EditingBanner from '../../components/chat/EditingBanner';
import DeleteConfirmModal from '../../components/chat/DeleteConfirmModal';

const ChatPage = () => {
  const { id: otherUserUid } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);

  const { user, firebaseUser } = useContext(AuthContext);
  const currentUserUid = user?.id || firebaseUser?.uid;

  // ── Debug Logs for Sender and Receiver IDs ───────────────────────────────
  useEffect(() => {
    if (currentUserUid || otherUserUid) {
      console.log('============= 💬 CHAT OPENED DEBUG INFO =============');
      console.log('👤 Sender (Current User DB ID):  ', currentUserUid);
      console.log('🔥 Firebase Auth User UID:       ', firebaseUser?.uid);
      console.log('👥 Receiver (Partner DB ID):     ', otherUserUid);
      console.log('🔑 Deterministic Room ID:        ', [String(currentUserUid), String(otherUserUid)].sort().join('_'));
      console.log('=====================================================');
    }
  }, [currentUserUid, otherUserUid, firebaseUser]);

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const [editingMessage, setEditingMessage] = useState(null);

  // ── Redux selectors ───────────────────────────────────────────────────────
  const { ui } = useSelector(state => state.chat);
  const { searchQuery, chatCustomization, isCustomizationOpen } = ui;
  const isOnline = useSelector(state => state.ui.networkStatus.isOnline);

  // ── Receiver meta (name + avatar, resolved from API on refresh) ───────────
  const { receiverName, receiverAvatar } = useReceiverMeta(
    currentUserUid,
    otherUserUid,
    { name: location.state?.name, avatar: location.state?.avatar }
  );

  // ── Firebase room + messages ──────────────────────────────────────────────
  const { messages, status, roomId, sendMessage, editMessage, deleteMessage, loadingOlder, hasMore, loadMore } = useChatRoom(
    currentUserUid,
    otherUserUid,
    { name: receiverName, avatar: receiverAvatar }
  );

  // ── Multi-select & action-menu state (all centralised in hook) ────────────
  const {
    activeActionMessageId,
    isSelectMode,
    selectedMessageIds,
    isDeleteModalOpen,
    deleteModalType,
    handleToggleActions,
    handleDelete,
    handleLongPress,
    handleSelectToggle,
    exitSelectMode,
    handleMassDeleteClick,
    confirmDelete,
    setIsDeleteModalOpen,
  } = useChatSelection(deleteMessage);

  // ── Load room-specific theme from localStorage ────────────────────────────
  useEffect(() => {
    if (!roomId) return;
    const stored = localStorage.getItem(`chatCustomization_${roomId}`);
    if (stored) {
      try {
        const theme = JSON.parse(stored);
        console.log(`🎨 [ChatPage] Applying stored theme for room "${roomId}":`, theme);
        dispatch(updateChatCustomization(theme));
      } catch (err) {
        console.warn('[ChatPage] Failed to parse stored room theme:', err);
      }
    }
  }, [roomId, dispatch]);

  const lastMsgIdRef = useRef(null);
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    const lastMsgId = lastMsg?.id ?? null;
    const isFirstLoad = lastMsgIdRef.current === null && messages.length > 0;
    const isNewMessage = lastMsgId !== null && lastMsgId !== lastMsgIdRef.current && lastMsgIdRef.current !== null;

    if (isFirstLoad || isNewMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: isFirstLoad ? 'auto' : 'smooth' });
    }

    lastMsgIdRef.current = lastMsgId;
  }, [messages]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const partnerPresence = usePresence(otherUserUid);
  const isPartnerOnline = partnerPresence.state === 'online';

  const activeConversation = useMemo(() => ({
    name:     receiverName,
    uid:      otherUserUid,
    avatar:   receiverAvatar,
    isOnline: isPartnerOnline,
  }), [receiverName, otherUserUid, receiverAvatar, isPartnerOnline]);

  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages;
    return messages.filter(msg =>
      (msg.content ?? '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [messages, searchQuery]);

  const chatBgStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${chatCustomization.backgroundColor.replace('#', '')}' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    backgroundColor: chatCustomization.backgroundColor,
  };

  // ── Edit handlers ─────────────────────────────────────────────────────────
  const handleSend = async (text) => {
    if (!text?.trim()) return;
    if (editingMessage) {
      await editMessage(editingMessage.id, text);
      setEditingMessage(null);
    } else {
      await sendMessage(text);
    }
  };

  const handleEditRequest = (message) => {
    setEditingMessage({ id: message.id, content: message.content });
  };

  // ── Offline guard ─────────────────────────────────────────────────────────
  if (!isOnline) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-6 text-center">
        <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-5">
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-3">Chat is Offline</h2>
        <p className="text-gray-500 text-sm max-w-[260px] leading-relaxed mb-6">
          You need an active internet connection to send and receive messages.
        </p>
        <button
          onClick={() => window.history.back()}
          className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const isLoading = status === 'loading' || status === 'idle';

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">

      {/* Header — swaps to SelectionHeader during multi-select */}
      {isSelectMode ? (
        <SelectionHeader
          selectedCount={selectedMessageIds.size}
          onExit={exitSelectMode}
          onDelete={handleMassDeleteClick}
        />
      ) : (
        <ChatHeader
          conversation={activeConversation}
          searchQuery={searchQuery}
          onSearchChange={e => dispatch(setSearchQuery(e.target.value))}
          onCustomizationToggle={() => dispatch(toggleCustomizationPanel())}
        />
      )}

      {/* Scrollable message area */}
      <ChatMessageList
        isLoading={isLoading}
        messages={messages}
        filteredMessages={filteredMessages}
        searchQuery={searchQuery}
        chatBgStyle={chatBgStyle}
        currentUserUid={currentUserUid}
        chatCustomization={chatCustomization}
        senderAvatar={user?.profile_image}
        receiverAvatar={receiverAvatar}
        isSelectMode={isSelectMode}
        selectedMessageIds={selectedMessageIds}
        activeActionMessageId={activeActionMessageId}
        onEditRequest={handleEditRequest}
        onDelete={handleDelete}
        onLongPress={handleLongPress}
        onSelectToggle={handleSelectToggle}
        onToggleActions={handleToggleActions}
        messagesEndRef={messagesEndRef}
        loadingOlder={loadingOlder}
        hasMore={hasMore}
        loadMore={loadMore}
      />

      {/* Edit-in-progress banner */}
      {editingMessage && (
        <EditingBanner
          content={editingMessage.content}
          onCancel={() => setEditingMessage(null)}
        />
      )}

      {/* Chat input */}
      <ChatInput
        onSend={handleSend}
        customization={chatCustomization}
        editingContent={editingMessage?.content}
        isEditing={!!editingMessage}
      />

      {/* Theme customisation panel */}
      <CustomizationPanel
        isOpen={isCustomizationOpen}
        onClose={() => dispatch(toggleCustomizationPanel())}
        roomId={roomId}
      />

      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        type={deleteModalType}
        count={selectedMessageIds.size}
      />

    </div>
  );
};

export default ChatPage;